import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Boxes, CheckCircle2, MapPin, PackageCheck, PauseCircle, PlayCircle, Printer, QrCode } from 'lucide-react';
import { formatDate, formatMoney, formatQty } from '@erp/shared';
import { api, errorMessage, openPdf } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PageHeader, ErrorState, EmptyState, DetailRow } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { Badge, StatusBadge } from '@/components/ui/badge';
import { HoldDialog } from './OrderActions';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Field, Input, Select } from '@/components/ui/input';
import { toast } from 'sonner';

interface Schedule {
  id: string; quantity: number; due_date: string; status: string;
  released_quantity: number; shipped_quantity: number;
}

interface OrderLine {
  id: string; line_no: number; quantity: number; unit_price: number;
  configuration: Record<string, unknown>; model_id: string; model_code: string; model_name: string;
  width_mm: number; height_mm: number; bom_version: number | null; released_units: number; schedules: Schedule[];
}

interface OrderUnit {
  id: string; qr_code: string; status: string; width_mm: number; height_mm: number;
  created_at: string; kit_id: string | null; kit_status: string | null; line_no: number;
}

interface AddressSnapshot {
  label?: string | null; recipient?: string | null; address?: string | null; city?: string | null;
  province?: string | null; postalCode?: string | null; country?: string | null;
}

interface OrderDetail {
  id: string; number: string; status: string; ordered_at: string; promised_date: string | null;
  on_hold: boolean; hold_reason: string | null; hold_at: string | null; hold_by_name: string | null;
  notes: string | null; totalValue: number;
  customer: { id: string; name: string; code: string; email: string | null; city: string | null; address: string | null };
  shippingAddress: AddressSnapshot | null;
  billingAddress: AddressSnapshot | null;
  lines: OrderLine[]; units: OrderUnit[];
}

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const { can } = useAuth();
  const [releaseLineId, setReleaseLineId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [holdOpen, setHoldOpen] = useState(false);
  const [working, setWorking] = useState(false);

  const orderQuery = useQuery({
    queryKey: ['order', id],
    queryFn: () => api<OrderDetail>(`/sales-orders/${id}`),
    enabled: Boolean(id)
  });

  const order = orderQuery.data;
  const releaseLine = order?.lines.find((line) => line.id === releaseLineId) ?? null;

  const confirm = async () => {
    setWorking(true);
    try {
      await api(`/sales-orders/${id}/confirm`, { method: 'POST' });
      toast.success('Pedido confirmado');
      setConfirmOpen(false);
      await qc.invalidateQueries({ queryKey: ['order', id] });
      await qc.invalidateQueries({ queryKey: ['orders'] });
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setWorking(false);
    }
  };

  const levantarRetencion = async () => {
    setWorking(true);
    try {
      await api(`/sales-orders/${id}/unhold`, { method: 'POST', body: {} });
      toast.success('Retención levantada');
      await qc.invalidateQueries({ queryKey: ['order', id] });
      await qc.invalidateQueries({ queryKey: ['orders'] });
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setWorking(false);
    }
  };

  if (orderQuery.error) return <ErrorState message={(orderQuery.error as Error).message} onRetry={() => void orderQuery.refetch()} />;
  if (orderQuery.isLoading || !order) {
    return <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-28 animate-pulse rounded-lg bg-muted" />)}</div>;
  }

  const totalQty = order.lines.reduce((acc, line) => acc + Number(line.quantity), 0);

  return (
    <div>
      {order.on_hold ? (
        <div className="mb-3 rounded-lg border border-warning/60 bg-warning/10 px-3 py-2 text-xs">
          <p className="font-medium">Pedido retenido: no avanza a producción ni se expide.</p>
          <p className="mt-0.5 text-muted-foreground">
            {order.hold_reason}
            {order.hold_by_name ? ` · ${order.hold_by_name}` : ''}
            {order.hold_at ? ` · ${formatDate(order.hold_at)}` : ''}
          </p>
        </div>
      ) : null}

      <div className="mb-3"><Link to="/orders" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><ArrowLeft className="size-3" /> Pedidos</Link></div>
      <PageHeader
        title={order.number}
        badge={(
          <div className="flex flex-wrap items-center gap-1">
            <StatusBadge status={order.status} />
            {order.on_hold ? <Badge tone="warning">Retenido</Badge> : null}
          </div>
        )}
        description={`${order.customer.name} · ${order.customer.code}`}
        actions={(
          <>
            {can('orders.write') && !order.on_hold && !['cancelled', 'shipped'].includes(order.status) ? (
              <Button variant="outline" onClick={() => setHoldOpen(true)}><PauseCircle /> Retener</Button>
            ) : null}
            {can('orders.write') && order.on_hold ? (
              <Button variant="outline" loading={working} onClick={() => void levantarRetencion()}><PlayCircle /> Levantar retención</Button>
            ) : null}
            {/* Mientras está retenido no se confirma ni se fabrica: el servidor lo rechaza. */}
            {order.status === 'draft' && can('orders.write') && !order.on_hold
              ? <Button onClick={() => setConfirmOpen(true)}><CheckCircle2 /> Confirmar pedido</Button> : null}
            {can('kits.read') && (order.status === 'confirmed' || order.status === 'in_production') && !order.on_hold ? (
              <Button variant="outline" onClick={() => setReleaseLineId(order.lines[0]?.id ?? null)}><QrCode /> Liberar unidades</Button>
            ) : null}
          </>
        )}

      />

      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <Card>
            <CardHeader title="Líneas del pedido" description={`${order.lines.length} ${order.lines.length === 1 ? 'línea' : 'líneas'} · ${formatQty(totalQty, 0)} unidades`} />
            <div className="divide-y divide-border">
              {order.lines.map((line) => {
                const remaining = Number(line.quantity) - line.released_units;
                return (
                  <div key={line.id} className="p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold">{line.model_name} <span className="font-mono text-[10px] text-muted-foreground">{line.model_code}</span></p>
                        <p className="text-[11px] text-muted-foreground">{line.width_mm}×{line.height_mm} mm · {formatMoney(line.unit_price)}/ud · BOM v{line.bom_version ?? '—'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge tone={remaining <= 0 ? 'success' : 'warning'}>{line.released_units}/{formatQty(line.quantity, 0)} liberadas</Badge>
                        {can('kits.read') && remaining > 0 && (order.status === 'confirmed' || order.status === 'in_production') ? (
                          <Button size="sm" variant="outline" onClick={() => setReleaseLineId(line.id)}>Liberar</Button>
                        ) : null}
                      </div>
                    </div>
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      {line.schedules.map((schedule) => (
                        <div key={schedule.id} className="rounded-md border border-border bg-muted/20 px-2.5 py-2 text-[11px]">
                          <div className="flex justify-between"><span className="text-muted-foreground">Entrega</span><span className="font-medium">{formatDate(schedule.due_date)}</span></div>
                          <div className="mt-1 flex justify-between"><span className="text-muted-foreground">Cantidad</span><span>{formatQty(schedule.quantity, 0)} · lib. {formatQty(schedule.released_quantity, 0)} · exp. {formatQty(schedule.shipped_quantity, 0)}</span></div>
                          <div className="mt-1"><StatusBadge status={schedule.status} /></div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card>
            <CardHeader title="Unidades y trazabilidad" description="QR interno de fabricación" actions={<Badge tone="outline">{order.units.length} unidades</Badge>} />
            {order.units.length === 0 ? (
              <EmptyState title="Sin unidades liberadas" description="Libera unidades para generar QR y kits." icon={Boxes} />
            ) : (
              <div className="overflow-x-auto">
                <table className="table-dense w-full">
                  <thead className="border-b border-border bg-muted/30"><tr><th>QR</th><th>Línea</th><th>Estado</th><th>Kit</th><th>Creada</th><th></th></tr></thead>
                  <tbody>
                    {order.units.map((unit) => (
                      <tr key={unit.id}>
                        <td className="font-mono text-xs">{unit.qr_code}</td>
                        <td>{unit.line_no}</td>
                        <td><StatusBadge status={unit.status} /></td>
                        <td>{unit.kit_id ? <Link to={`/kits/${unit.kit_id}`} className="inline-flex hover:opacity-80"><StatusBadge status={unit.kit_status} /></Link> : '—'}</td>
                        <td className="text-[10px] text-muted-foreground">{new Date(unit.created_at).toLocaleString('es-ES')}</td>
                        <td className="text-right">
                          <Button variant="ghost" size="icon-sm" title="Etiqueta QR" onClick={() => void openPdf(`/mirror-units/${unit.id}/label.pdf`)}><Printer /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-4">
          {confirmOpen ? (
            <Card>
              <CardHeader title="Confirmar pedido" />
              <CardContent className="space-y-3 text-xs">
                <p className="text-muted-foreground">El pedido pasará a demanda confirmada y se tendrá en cuenta en la planificación.</p>
                <p>Pedido <span className="font-mono font-medium">{order.number}</span> · {formatQty(totalQty, 0)} unidades.</p>
                <Card>
            <CardHeader title="Cliente" />
            <CardContent className="text-xs">
              <DetailRow label="Nombre" value={order.customer.name} />
              <DetailRow label="Código" value={order.customer.code} />
              <DetailRow label="Email" value={order.customer.email ?? '—'} />
              <DetailRow label="Ciudad" value={order.customer.city ?? '—'} />
              <DetailRow label="Facturación" value={(
                <span>
                  {order.billingAddress?.address ?? order.customer.address ?? '—'}
                  <br />
                  {[order.billingAddress?.postalCode, order.billingAddress?.city].filter(Boolean).join(' ') || ''}
                </span>
              )} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader title="Dirección de envío" actions={<MapPin className="size-4 text-primary" />} />
            <CardContent className="text-xs">
              {order.shippingAddress ? (
                <>
                  <p className="font-semibold text-foreground">{order.shippingAddress.label ?? 'Envío'}</p>
                  {order.shippingAddress.recipient ? <p className="mt-0.5 text-muted-foreground">A/A: {order.shippingAddress.recipient}</p> : null}
                  <p className="mt-2">{order.shippingAddress.address ?? '—'}</p>
                  <p>{[order.shippingAddress.postalCode, order.shippingAddress.city, order.shippingAddress.province].filter(Boolean).join(' · ')}</p>
                  <p className="text-muted-foreground">{order.shippingAddress.country ?? 'ES'}</p>
                </>
              ) : <p className="text-muted-foreground">Usa la dirección de facturación.</p>}
            </CardContent>
          </Card>
          <Card>
            <CardHeader title="Resumen" />
            <CardContent className="text-xs">
              <DetailRow label="Pedido" value={formatDate(order.ordered_at)} />
              <DetailRow label="Entrega" value={formatDate(order.promised_date)} />
              <DetailRow label="Unidades" value={formatQty(totalQty, 0)} />
              <DetailRow label="Valor" value={formatMoney(order.totalValue)} />
              <DetailRow label="Notas" value={order.notes ?? '—'} />
            </CardContent>
          </Card>
          <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => setConfirmOpen(false)}>Cancelar</Button>
                  <Button size="sm" className="flex-1" loading={working} onClick={() => void confirm()}>Confirmar</Button>
                </div>
              </CardContent>
            </Card>
          ) : null}

          {releaseLineId ? (
            <ReleasePanel
              orderId={order.id}
              lines={order.lines}
              initialLineId={releaseLineId}
              onCancel={() => setReleaseLineId(null)}
              onReleased={async (count) => {
                setReleaseLineId(null);
                toast.success(`${count} unidad(es) liberadas con QR y kit`);
                await qc.invalidateQueries({ queryKey: ['order', id] });
                await qc.invalidateQueries({ queryKey: ['orders'] });
                await qc.invalidateQueries({ queryKey: ['kits'] });
              }}
            />
          ) : null}

          <div className="flex gap-2">
            <Link to="/kits" className="flex-1"><Button variant="outline" size="sm" className="w-full"><PackageCheck /> Ir a kits</Button></Link>
            <Link to="/packaging" className="flex-1"><Button variant="outline" size="sm" className="w-full">Embalaje</Button></Link>
          </div>
        </div>
      </div>

      <HoldDialog
        orderId={id ?? null}
        number={order.number}
        open={holdOpen}
        onOpenChange={setHoldOpen}
        onDone={() => { setHoldOpen(false); void qc.invalidateQueries({ queryKey: ['order', id] }); void qc.invalidateQueries({ queryKey: ['orders'] }); }}
      />
    </div>
  );
}

function ReleasePanel({ orderId, lines, initialLineId, onCancel, onReleased }: {
  orderId: string; lines: OrderLine[]; initialLineId: string;
  onCancel: () => void; onReleased: (count: number) => Promise<void>;
}) {
  const [lineId, setLineId] = useState(initialLineId);
  const [quantity, setQuantity] = useState('1');
  const [saving, setSaving] = useState(false);

  const line = lines.find((item) => item.id === lineId);
  const remaining = line ? Number(line.quantity) - line.released_units : 0;

  const submit = async () => {
    setSaving(true);
    try {
      const result = await api<{ created: unknown[] }>(`/sales-orders/${orderId}/release`, {
        method: 'POST', body: { orderLineId: lineId, quantity: Number(quantity) }
      });
      await onReleased(result.created.length);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader title="Liberar unidades" description="Genera QR, kit y reservas" />
      <CardContent className="space-y-3">
        <Field label="Línea">
          <Select value={lineId} onChange={(e) => { setLineId(e.target.value); setQuantity('1'); }}>
            {lines.map((item) => <option key={item.id} value={item.id}>{item.line_no} · {item.model_name} ({item.released_units}/{item.quantity})</option>)}
          </Select>
        </Field>
        <Field label="Cantidad" hint={line ? `${remaining} pendientes` : undefined}>
          <Input type="number" min="1" max={remaining} value={quantity} onChange={(e) => setQuantity(e.target.value)} />
        </Field>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex-1" onClick={onCancel}>Cancelar</Button>
          <Button size="sm" className="flex-1" loading={saving} disabled={!lineId || remaining <= 0} onClick={() => void submit()}>Liberar</Button>
        </div>
      </CardContent>
    </Card>
  );
}